import { useMemo, useState } from "react";
import { characters } from "@/mocks/home";

const flameColor: Record<string, string> = {
  大空: "bg-primary-100 text-primary-800",
  岚: "bg-accent-100 text-accent-800",
  雨: "bg-primary-100 text-primary-700",
  雷: "bg-accent-100 text-accent-700",
  云: "bg-secondary-100 text-secondary-900",
  雾: "bg-secondary-100 text-secondary-800",
  晴: "bg-secondary-100 text-secondary-700",
  "—": "bg-background-200 text-foreground-700",
};

export default function CharactersSection() {
  const [keyword, setKeyword] = useState("");
  const [flame, setFlame] = useState("全部");

  const flameOptions = useMemo(() => {
    const set = new Set(characters.map((c) => c.flame));
    return ["全部", ...Array.from(set)];
  }, []);

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return characters.filter((c) => {
      const matchFlame = flame === "全部" || c.flame === flame;
      const matchKw =
        !kw ||
        c.name.toLowerCase().includes(kw) ||
        c.alias.toLowerCase().includes(kw) ||
        c.role.toLowerCase().includes(kw) ||
        c.tags.some((t) => t.toLowerCase().includes(kw));
      return matchFlame && matchKw;
    });
  }, [keyword, flame]);

  return (
    <section id="characters" className="section-anchor w-full bg-background-50 py-16 md:py-24">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        <div className="reveal max-w-2xl">
          <span className="text-xs font-label tracking-[0.2em] text-accent-600">主要角色 / CHARACTERS</span>
          <h2 className="mt-3 font-heading text-3xl md:text-4xl text-foreground-950">
            并盛町的伙伴们与守护者
          </h2>
          <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-700">
            从家庭教师里包恩，到六位不同属性的守护者，每位角色都有自己的性格与立场。
            你可以按关键词搜索，或按死气之炎属性筛选，快速找到感兴趣的人物。
          </p>
        </div>

        <div className="reveal mt-8 flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-6">
          <div className="relative w-full lg:max-w-xs">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索角色名、身份或标签"
              className="w-full pl-9 pr-3 py-2.5 rounded-md bg-background-100 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {flameOptions.map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFlame(f)}
                className={`px-3.5 py-2 rounded-full text-xs md:text-sm whitespace-nowrap transition-colors ${
                  flame === f
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-secondary-100 border border-background-200"
                }`}
              >
                {f === "全部" ? "全部属性" : `${f}之炎`}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
          {filtered.map((c) => (
            <article
              key={c.name}
              className="rounded-lg bg-background-100 border border-background-200 p-4 md:p-5 flex flex-col"
            >
              <div className="flex items-start gap-4">
                <div className="shrink-0 w-14 h-14 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center font-heading text-xl">
                  {c.name.slice(0, 1)}
                </div>
                <div className="min-w-0">
                  <h3 className="font-heading text-lg text-foreground-950">{c.name}</h3>
                  <p className="text-xs text-foreground-500 mt-0.5 truncate">{c.alias}</p>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className={`px-2.5 py-1 rounded-full text-xs ${flameColor[c.flame] || flameColor["—"]}`}>
                  {c.flame === "—" ? "非守护者" : `${c.flame}之炎`}
                </span>
                <span className="px-2.5 py-1 rounded-full text-xs bg-background-200 text-foreground-700">
                  {c.role}
                </span>
              </div>
              <p className="mt-3 text-xs md:text-sm leading-relaxed text-foreground-600 flex-1">{c.desc}</p>
              <div className="mt-3 pt-3 border-t border-background-200 flex flex-wrap gap-1.5">
                {c.tags.map((t) => (
                  <span key={t} className="text-xs text-secondary-700">
                    #{t}
                  </span>
                ))}
              </div>
            </article>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="mt-10 text-center py-12 rounded-lg bg-background-100 border border-dashed border-background-300">
            <i className="ri-search-eye-line text-3xl text-foreground-400" />
            <p className="mt-3 text-sm text-foreground-600">没有找到匹配的角色，换个关键词或属性试试。</p>
            <button
              type="button"
              onClick={() => {
                setKeyword("");
                setFlame("全部");
              }}
              className="mt-4 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm"
            >
              重置筛选
            </button>
          </div>
        )}
      </div>
    </section>
  );
}